import type {
  GraphemeMorphemeMnemonicsPanelMorphemeSlice,
  GraphemeMorphemeMnemonicsPanelMorphemeUserSlice,
} from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { useResolvedQuery } from "@-/dev.improve.haniverse.lib.query-options";
import type { DataTag, QueryKey } from "@-/com.npmjs.tanstack__react-query";
import type { ReactNode } from "react";
import { GraphemeMorphemePanelsContextProvider } from "#grapheme-morpheme-panels-context-provider";

/**
 * Publishes `GraphemeMorphemePanelsContext` from a gate: the component that observed both slice
 * queries and already rendered its not-found state for a `null` readonly slice. Takes the narrowed
 * data plus each query's key and builds the {@link ResolvedQuery} handles the context carries.
 */
export function GraphemeMorphemePanelsGateProvider({
  children,
  readonlySlice,
  readonlySliceQueryKey,
  userSlice,
  userSliceQueryKey,
}: {
  children: ReactNode;
  readonlySlice: GraphemeMorphemeMnemonicsPanelMorphemeSlice;
  readonlySliceQueryKey: DataTag<
    QueryKey,
    GraphemeMorphemeMnemonicsPanelMorphemeSlice | null,
    Error
  >;
  userSlice: GraphemeMorphemeMnemonicsPanelMorphemeUserSlice;
  userSliceQueryKey: DataTag<QueryKey, GraphemeMorphemeMnemonicsPanelMorphemeUserSlice, Error>;
}) {
  const resolvedReadonlySlice = useResolvedQuery(readonlySliceQueryKey, readonlySlice);
  const resolvedUserSlice = useResolvedQuery(userSliceQueryKey, userSlice);
  return (
    <GraphemeMorphemePanelsContextProvider
      readonlySlice={resolvedReadonlySlice}
      userSlice={resolvedUserSlice}
    >
      {children}
    </GraphemeMorphemePanelsContextProvider>
  );
}
